import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";

const PER_PAGE = 6;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") || "";
  const [input, setInput] = useState(queryParam);
  const [tab, setTab] = useState<"movie" | "news">("movie");
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: queryParam
      ? `“${queryParam}”的搜索结果 - 午夜场的电影`
      : "搜索午夜电影与影视资讯 - 午夜场的电影",
    description: "在午夜场的电影站内搜索午夜影片与影视资讯，支持按电影和资讯两类结果切换查看。",
    keywords: "午夜电影搜索,影视资讯搜索,午夜场的电影",
  });

  useEffect(() => {
    setInput(queryParam);
    setPage(1);
    setTab("movie");
  }, [queryParam]);

  const keyword = queryParam.trim().toLowerCase();

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    return movies.filter((movie) =>
      [movie.title, movie.summary, movie.director, ...movie.genres, ...movie.tags]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return news.filter((item) =>
      [item.title, item.summary, item.category, item.source]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const activeResults: (typeof movies | typeof news) =
    tab === "movie" ? movieResults : newsResults;
  const totalPages = Math.ceil((activeResults as unknown[]).length / PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages, 1));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    if (!q) return;
    searchParams.set("q", q);
    setSearchParams(searchParams);
  };

  const recommendations = movies.filter((movie) => movie.featured).slice(0, 4);
  const hasQuery = keyword.length > 0;
  const hasResults = (activeResults as unknown[]).length > 0;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 lg:px-10">
      <header className="mb-8">
        <h1 className="mb-4 font-heading text-3xl font-extrabold tracking-tight text-foreground-50 md:text-4xl">
          搜索
        </h1>
        <form onSubmit={submit} className="flex w-full max-w-2xl items-center gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 focus-within:border-primary-500">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入片名、类型、导演或资讯关键词"
              className="w-full bg-transparent text-sm text-foreground-50 outline-none placeholder:text-foreground-600"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
        {hasQuery && (
          <p className="mt-4 text-sm text-foreground-500">
            关键词 <span className="font-semibold text-primary-400">“{queryParam}”</span> 的搜索结果
          </p>
        )}
      </header>

      {hasQuery && (
        <div className="mb-6 flex items-center gap-2 border-b border-background-200">
          {[
            { key: "movie" as const, label: "电影", count: movieResults.length },
            { key: "news" as const, label: "资讯", count: newsResults.length },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                setTab(item.key);
                setPage(1);
              }}
              className={`relative -mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                tab === item.key
                  ? "border-primary-500 text-primary-400"
                  : "border-transparent text-foreground-500 hover:text-foreground-100"
              }`}
            >
              {item.label}
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] ${
                  tab === item.key
                    ? "bg-primary-500/15 text-primary-400"
                    : "bg-background-200/70 text-foreground-600"
                }`}
              >
                {item.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {!hasQuery ? (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
          <span className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-3xl text-foreground-500">
            <i className="ri-search-2-line"></i>
          </span>
          <h3 className="mb-2 font-heading text-lg font-bold text-foreground-100">
            输入关键词开始搜索
          </h3>
          <p className="text-sm text-foreground-600">
            试试“惊悚”“午夜”“剧情”或某部影片的名字
          </p>
        </div>
      ) : hasResults ? (
        tab === "movie" ? (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {(movieResults.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)).map(
                (movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} />
                ),
              )}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <>
            <div className="space-y-5">
              {(newsResults.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)).map(
                (item, index) => (
                  <NewsCard key={item.id} item={item} index={index} />
                ),
              )}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        )
      ) : (
        <div>
          <div className="mb-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
            <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-2xl text-foreground-500">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h3 className="mb-2 font-heading text-lg font-bold text-foreground-100">
              没有找到与“{queryParam}”相关的结果
            </h3>
            <p className="text-sm text-foreground-600">
              换个关键词试试，或者看看下面这些热门午夜电影推荐
            </p>
          </div>

          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
            <i className="ri-fire-line text-primary-400"></i>
            热门午夜电影推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {recommendations.map((movie, index) => (
              <MovieCard key={movie.id} movie={movie} index={index} />
            ))}
          </div>

          <div className="mt-8">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
            >
              <i className="ri-article-line"></i>
              浏览全部影视资讯
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}